import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "__chunk_reload_attempted";

/**
 * React.lazy wrapper that survives stale chunk hashes after a new deploy.
 * Retries the dynamic import once (cache-busted); if that fails, reloads once.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      const msg = String((error as Error)?.message || "");
      const isChunkError =
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module");

      if (!isChunkError) throw error;

      try {
        return await factory();
      } catch {
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
        }
        // Keep Suspense pending while the page reloads.
        return await new Promise<{ default: T }>(() => {});
      }
    }
  });
}
